import { TPosSystemId, TTlsCertificateLevel } from 'types';

export interface IGetPaginatedDevices {
	pageNumber: number;
	pageSize: number;
	searchByName: string | null;
	customerId: string | null;
	restaurantId: string | null;
	sortByHardwareId: string | null;
	sortByRestaurantAddress: string | null;
	sortByCustomerName: string | null;
}

export interface IItemDeviceBankSetting {
	terminalId: string | null;
	restaurantBankSetting: { acquirerName: string | null };
}

export interface IItemDevice {
	id: string;
	hardwareId: string | null;
	restaurantId: string;
	restaurantAddress: string | null;
	customerId: string;
	customerName: string | null;
	deviceBankSettings: IItemDeviceBankSetting[];
	tableName: Nullable<string>;
}

export interface IDeviceBankSetting {
	id: string;
	terminalId: string | null;
	deviceId: string;
	restaurantBankSettingId: string | null;
	restaurantBankSetting: IAvailableRestaurantBankSettings;
	m4BankSettingCredentials: Nullable<IM4BankSettingCredentialsCreate>;
	toucanBankSettingCredentials: Nullable<IToucanBankSettingCredentials>;
}

export interface IM4BankSettingCredentialsCreate {
	login: Nullable<string>;
	password: Nullable<string>;
}

export interface IToucanBankSettingCredentials {
	login: Nullable<string>;
	password: Nullable<string>;
	isPasswordUpdated?: Nullable<boolean>;
}

export interface IDeviceBankSettingForCreate {
	id: string | null;
	terminalId: string | null;
	restaurantBankSettingId: string | null;
	m4BankSettingCredentials: Nullable<IM4BankSettingCredentialsCreate>;
	toucanBankSettingCredentials: Nullable<IToucanBankSettingCredentials>;
}

export interface IM4BankSettingCredentialsUpdate extends IM4BankSettingCredentialsCreate {
	isPasswordUpdated: Nullable<boolean>;
}

export interface IDeviceBankSettingForEdit extends IDeviceBankSettingForCreate {
	m4BankSettingCredentials: Nullable<IM4BankSettingCredentialsUpdate>;
	toucanBankSettingCredentials: Nullable<IToucanBankSettingCredentials>;
}

export interface IAvailableRestaurantBankSettings {
	id: string;
	acquirerId: string;
	acquirerName: string;
}

export interface IItemDeviceForGetInfo {
	id: string;
	hardwareId: string | null;
	restaurantId: string | null;
	customerId: string | null;
	posSystemId: TPosSystemId;
	insertedBy: string | null;
	updatedBy: string | null;
	insertedDate: string;
	updatedDate: string | null;
	deviceBankSettings: IDeviceBankSetting[];
	availableRestaurantBankSettings: IAvailableRestaurantBankSettings[];
	tableName: Nullable<string>;
	useTlsCertificate: boolean;
	tlsCertificateLevel: TTlsCertificateLevel;
	tlsCertificateFilePath: Nullable<string>;
	tlsCertificateName: Nullable<string>;
}

export interface IDevices {
	items: IItemDevice[];
	count: number;
}

export interface IGetDeviceInfo {
	data: IItemDeviceForGetInfo;
}

export interface ICreateDevice {
	id: string | null;
	hardwareId: string;
	restaurantId: string | null;
	deviceBankSettings: IDeviceBankSettingForCreate[];
	tlsCertificateName: Nullable<string>;
	tlsCertificateFileId: Nullable<string>;
	tlsCertificateFileHash: Nullable<string>;
}

export interface IEditDevice {
	id: string;
	hardwareId: string;
	restaurantId: string | null;
	deviceBankSettings: IDeviceBankSettingForEdit[];
	tlsCertificateName: Nullable<string>;
	tlsCertificateFileId: Nullable<string>;
	tlsCertificateFileHash: Nullable<string>;
}

export enum DevicesNotificationTypes {
	PushDropBoxUploadFilePull = 'PushDropBoxUploadFilePull',
	PushDropBoxUploadLauncherFilePull = 'PushDropBoxUploadLauncherFilePull',
	PushDeactivateDevice = 'PushDeactivateDevice',
	PushUpdateMainSettings = 'PushUpdateMainSettings',
	PushUpdateConfigurationFile = 'PushUpdateConfigurationFile',
	PushUpdateAllSettings = 'PushUpdateAllSettings',
	PushUpdateRestaurantSettings = 'PushUpdateRestaurantSettings',
	PushUpdateEncryptedPaymentConfig = 'PushUpdateEncryptedPaymentConfig',
	PushShutdownDevices = 'PushShutdownDevices',
	PushRebootDevices = 'PushRebootDevices',
	PushDownloadFirmware = 'PushDownloadFirmware',
	PushRestartApp = 'PushRestartApp',
	PushCloseDay = 'PushCloseDay',
	PushUpdateRestaurantMenu = 'PushUpdateRestaurantMenu',
	PushRestaurantMenu = 'PushRestaurantMenu',
	PushUpdateGames = 'PushUpdateGames',
	PushUpdateTeasers = 'PushUpdateTeasers',
	PushUpdateLauncherConfig = 'PushUpdateLauncherConfig',
	PushUpdateVideos = 'PushUpdateVideos',
	PushDmsNetworkParams = 'PushDmsNetworkParams',
	PushSendThirdPartyLogs = 'PushSendThirdPartyLogs',
}

export enum DevicesNotificationFileTypes {
	TOMBSTONES = 'TOMBSTONES',
	PS_LOGS = 'PS_LOGS',
	PS_DATABASE = 'PS_DATABASE',
}

export type TDropboxPushMessage = Exclude<DevicesNotificationTypes, DevicesNotificationTypes.PushDeactivateDevice>;

export interface IDropboxPushMessage {
	value: TDropboxPushMessage;
	name: string;
}

export interface IDeviceNotificationsData {
	restaurantId: string;
	notificationType: DevicesNotificationTypes;
	fileType?: DevicesNotificationFileTypes;
	params?: string;
	metadata?: { removeContent?: boolean; clearRestaurant?: boolean; isBackupEnabled?: boolean };
}
